import { Injectable, Injector, OnDestroy } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { ROUTE_REUSE_PAIRS } from '../constant/route-pair.constants';

/**
 * NavigationPairResolverService
 *
 * Listens to Router NavigationEnd events, tracks the previous URL, and
 * evaluates whether a given URL or URL-pair matches any entry in
 * ROUTE_REUSE_PAIRS (FR-5: centralised navigation evaluation service).
 *
 * WHY Injector instead of Router in the constructor:
 * ───────────────────────────────────────────────────
 * CustomRouteReuseStrategy is registered as { provide: RouteReuseStrategy }.
 * Angular's Router reads RouteReuseStrategy during its own construction,
 * creating the cycle:
 *
 *   Router → RouteReuseStrategy → CustomRouteReuseStrategy
 *          → NavigationPairResolverService → Router   ← NG0200
 *
 * Injecting Injector (which has no such cycle) and resolving Router lazily
 * on first use — after the full DI graph is built — breaks the cycle cleanly.
 */
@Injectable({ providedIn: 'root' })
export class NavigationPairResolverService implements OnDestroy {
    private previousUrl: string | null = null;
    private currentUrl: string | null = null;
    private routerSub: Subscription | null = null;
    private _router: Router | null = null;

    constructor(private readonly injector: Injector) { }

    // ── Lazy Router access ────────────────────────────────────────────────────

    /**
     * Router is resolved on first access, guaranteeing the DI graph is fully
     * initialised before we touch Router.
     */
    private get router(): Router {
        if (!this._router) {
            this._router = this.injector.get(Router);
            this.routerSub = this._router.events
                .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
                .subscribe((event: NavigationEnd) => {
                    this.previousUrl = this.currentUrl;
                    this.currentUrl = this.normalise(event.urlAfterRedirects);
                });
        }
        return this._router;
    }

    ngOnDestroy(): void {
        this.routerSub?.unsubscribe();
    }

    // ── Public API ────────────────────────────────────────────────────────────

    /** Returns the URL of the page navigated away from, or null on first load. */
    getPreviousUrl(): string | null {
        void this.router; // ensure subscription is active
        return this.previousUrl;
    }

    /** Returns the currently active URL. */
    getCurrentUrl(): string | null {
        void this.router; // ensure subscription is active
        return this.currentUrl;
    }

    /**
     * Returns true if `url` starts with any configured fromPath.
     * Prefix matching handles query strings and child segments automatically.
     */
    isValidFromPath(url: string): boolean {
        const normalised = this.normalise(url);
        return ROUTE_REUSE_PAIRS.some(pair =>
            normalised.startsWith(pair.fromPath)
        );
    }

    /**
     * Returns true if `url` starts with any configured toPath.
     */
    isValidToPath(url: string): boolean {
        const normalised = this.normalise(url);
        return ROUTE_REUSE_PAIRS.some(pair =>
            normalised.startsWith(pair.toPath)
        );
    }

    /**
     * Returns true if `fromUrl` and `toUrl` together form a configured pair.
     */
    isValidPair(fromUrl: string, toUrl: string): boolean {
        const from = this.normalise(fromUrl);
        const to = this.normalise(toUrl);
        return ROUTE_REUSE_PAIRS.some(pair =>
            from.startsWith(pair.fromPath) && to.startsWith(pair.toPath)
        );
    }

    /**
     * Convenience method used by the strategy: checks whether the tracked
     * previous URL and the supplied `toUrl` form a valid pair.
     */
    previousToCurrent(toUrl: string): boolean {
        if (!this.previousUrl) {
            return false;
        }
        return this.isValidPair(this.previousUrl, toUrl);
    }

    // ── Private helpers ───────────────────────────────────────────────────────

    /**
     * Strips leading '#', '/', query params, and fragments to produce a clean
     * path for prefix matching against the constants.
     */
    private normalise(url: string): string {
        return url
            .replace(/^[/#]+/, '')  // strip leading # and /
            .split('?')[0]           // drop query params
            .split('#')[0];          // drop inline fragments
    }
}
